import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";

import { getVoteById, submitVote } from "../../../services/voteServices";
import {
  FaCheck,
  FaPaperPlane,
  FaSpinner,
  FaTimes,
  FaVoteYea,
} from "react-icons/fa";

function QuestionText({ value }) {
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <ReactQuill value={value || ""} readOnly theme="bubble" />
    </div>
  );
}

function ChoiceOption({ checked, children, type = "radio", onChange }) {
  return (
    <label
      className={[
        "flex min-h-12 cursor-pointer items-center gap-3 rounded-2xl border px-3 py-2 text-sm font-semibold transition",
        checked
          ? "border-violet-300 bg-violet-50 text-violet-900 shadow-sm dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-100"
          : "border-slate-200 bg-white text-slate-700 hover:border-violet-200 hover:bg-violet-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:border-violet-700 dark:hover:bg-violet-950/30",
      ].join(" ")}
    >
      <input
        type={type}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 accent-violet-600"
      />

      <span className="flex-1">{children}</span>

      {checked && <FaCheck className="text-violet-600 dark:text-violet-300" />}
    </label>
  );
}

export default function ParticipateVoteModal({
  isOpen,
  onClose,
  voteId,
  userId,
  refreshVotes,
}) {
  const [vote, setVote] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) {
        onClose?.();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose, submitting]);

  useEffect(() => {
    if (!isOpen || !voteId) {
      setVote(null);
      setResponses({});
      setSubmitting(false);
      return;
    }

    let mounted = true;

    async function loadVote() {
      try {
        setLoading(true);
        const data = await getVoteById(voteId);

        if (!mounted) return;

        setVote(data);
        setResponses({});
      } catch (error) {
        console.error("Erreur chargement vote :", error);
        toast.error("Erreur lors du chargement du vote");
        onClose?.();
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadVote();

    return () => {
      mounted = false;
    };
  }, [isOpen, onClose, voteId]);

  const questions = useMemo(() => vote?.questions || [], [vote]);

  const handleChange = (questionId, value, type) => {
    if (type === "multiple") {
      setResponses((previousResponses) => {
        const previousChoices = Array.isArray(previousResponses[questionId])
          ? previousResponses[questionId]
          : [];

        return {
          ...previousResponses,
          [questionId]: previousChoices.includes(value)
            ? previousChoices.filter((choiceId) => choiceId !== value)
            : [...previousChoices, value],
        };
      });

      return;
    }

    setResponses((previousResponses) => ({
      ...previousResponses,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!voteId || !userId || submitting) return;

    const payload = {
      vote_id: voteId,
      user_id: userId,
      responses,
    };

    try {
      setSubmitting(true);

      const ok = await submitVote(payload);

      if (!ok) {
        throw new Error("Échec de la soumission");
      }

      toast.success("Réponses enregistrées !");
      onClose?.();
      refreshVotes?.();
    } catch (error) {
      console.error("Erreur soumission vote :", error);
      toast.error("Erreur lors de l'envoi des réponses");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm"
        onClick={submitting ? undefined : onClose}
      />

      <div className="relative z-[101] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-950 sm:max-w-3xl sm:rounded-[2rem]">
        <div className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white sm:px-7">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-violet-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-sky-200">
                <FaVoteYea />
                Participation
              </p>

              <h3 className="mt-2 text-xl font-bold sm:text-2xl">
                {loading ? "Chargement du vote..." : vote?.title || "Vote"}
              </h3>

              {!!vote?.description && (
                <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-200">
                  {vote.description}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading && (
            <div className="space-y-4">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  className="h-36 animate-pulse rounded-[1.5rem] bg-slate-100 dark:bg-slate-900"
                />
              ))}
            </div>
          )}

          {!loading && questions.length === 0 && (
            <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-900/60">
              <p className="font-bold text-slate-950 dark:text-white">
                Aucune question disponible.
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Ce vote ne contient pas encore de question.
              </p>
            </div>
          )}

          {!loading && questions.length > 0 && (
            <div className="space-y-4">
              {questions.map((question, index) => {
                const value = responses[question.id];

                return (
                  <div
                    key={question.id}
                    className="rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-900/60"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-bold text-white dark:bg-white dark:text-slate-950">
                        Question {index + 1}
                      </span>

                      <span className="text-xs font-semibold text-slate-400">
                        {question.type === "open"
                          ? "Réponse libre"
                          : question.type === "multiple"
                          ? "Choix multiple"
                          : "Choix unique"}
                      </span>
                    </div>

                    <QuestionText value={question.question_text} />

                    {question.type === "open" && (
                      <textarea
                        rows={4}
                        placeholder="Votre réponse"
                        value={value || ""}
                        onChange={(event) =>
                          handleChange(
                            question.id,
                            event.target.value,
                            question.type
                          )
                        }
                        className="mt-3 min-h-28 w-full rounded-2xl border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-violet-900/50"
                      />
                    )}

                    {question.type === "single" && (
                      <div className="mt-3 space-y-2">
                        {(question.choices || []).map((choice) => (
                          <ChoiceOption
                            key={choice.id}
                            type="radio"
                            checked={value === choice.id}
                            onChange={() =>
                              handleChange(question.id, choice.id, question.type)
                            }
                          >
                            {choice.choice_text}
                          </ChoiceOption>
                        ))}
                      </div>
                    )}

                    {question.type === "multiple" && (
                      <div className="mt-3 space-y-2">
                        {(question.choices || []).map((choice) => {
                          const checked =
                            Array.isArray(value) && value.includes(choice.id);

                          return (
                            <ChoiceOption
                              key={choice.id}
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                handleChange(
                                  question.id,
                                  choice.id,
                                  question.type
                                )
                              }
                            >
                              {choice.choice_text}
                            </ChoiceOption>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:p-4">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || loading || questions.length === 0}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-4 text-sm font-bold text-white shadow-lg shadow-violet-200 transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 dark:shadow-black/20"
            >
              {submitting ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              {submitting ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}